/**
 * Shared types and validation for Previous Projects
 * Used by both frontend and backend to ensure consistency
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Project type categories for previous projects
 */
export const PREVIOUS_PROJECT_TYPES = [
	"residential",
	"commercial",
	"industrial",
	"office",
	"retail",
	"hospitality",
	"other",
] as const;

export type PreviousProjectType = (typeof PREVIOUS_PROJECT_TYPES)[number];

/**
 * Project status options
 */
export const PREVIOUS_PROJECT_STATUSES = [
	"completed",
	"in_progress",
	"on_hold",
	"planned",
] as const;

export type PreviousProjectStatus = (typeof PREVIOUS_PROJECT_STATUSES)[number];

// ============================================================================
// VALIDATION RULES
// ============================================================================

/**
 * Validation rules for PreviousProject fields
 * Shared between frontend (Zod) and backend (custom validation)
 */
export const PREVIOUS_PROJECT_VALIDATION = {
	title: {
		required: true,
		maxLength: 200,
		minLength: 1,
	},
	description: {
		required: false,
		maxLength: 5000,
	},
	year: {
		required: false,
		min: 1900,
		max: new Date().getFullYear() + 1,
	},
	location: {
		required: false,
		maxLength: 200,
	},
	budgetMin: {
		required: false,
		min: 0,
	},
	budgetMax: {
		required: false,
		min: 0,
	},
	durationMonths: {
		required: false,
		min: 1,
		max: 120, // 10 years max
	},
	projectType: {
		required: false,
		enum: PREVIOUS_PROJECT_TYPES,
	},
	status: {
		required: false,
		enum: PREVIOUS_PROJECT_STATUSES,
	},
	imageUrls: {
		required: false,
		maxItems: 6,
	},
} as const;

// ============================================================================
// BUSINESS LOGIC
// ============================================================================

/**
 * Validates that budget range is logical (min <= max)
 * Undefined values are considered valid (optional field)
 */
export function isValidBudgetRange(
	min: number | undefined | null,
	max: number | undefined | null,
): boolean {
	if (min == null || max == null) return true;
	return min <= max;
}

/**
 * Formats a number as Indian Rupees with proper notation
 * Example: 100000 -> "₹1,00,000"
 */
export function formatBudgetINR(amount: number): string {
	return new Intl.NumberFormat("en-IN", {
		style: "currency",
		currency: "INR",
		maximumFractionDigits: 0,
	}).format(amount);
}

/**
 * Formats budget range for display
 * Example: { min: 1000000, max: 1500000 } -> "₹10,00,000 - ₹15,00,000"
 */
export function formatBudgetRange(
	min: number | undefined | null,
	max: number | undefined | null,
): string {
	if (min == null && max == null) return "Not specified";
	if (min == null && max != null) return `Up to ${formatBudgetINR(max)}`;
	if (max == null && min != null) return `From ${formatBudgetINR(min)}`;
	// Both min and max are non-null here
	return `${formatBudgetINR(min as number)} - ${formatBudgetINR(max as number)}`;
}

/**
 * Validates project year is within acceptable range
 */
export function isValidYear(year: number): boolean {
	const { min, max } = PREVIOUS_PROJECT_VALIDATION.year;
	return year >= min && year <= max;
}

/**
 * Validates project type is a valid enum value
 */
export function isValidProjectType(type: string): type is PreviousProjectType {
	return PREVIOUS_PROJECT_TYPES.includes(type as PreviousProjectType);
}

/**
 * Validates project status is a valid enum value
 */
export function isValidProjectStatus(
	status: string,
): status is PreviousProjectStatus {
	return PREVIOUS_PROJECT_STATUSES.includes(status as PreviousProjectStatus);
}

/**
 * Maximum number of previous projects allowed per profile
 */
export const MAX_PREVIOUS_PROJECTS = 10;

/**
 * Default sort order for new projects (top of list)
 */
export const DEFAULT_SORT_ORDER = 0;

/**
 * Validates that the number of projects doesn't exceed maximum
 */
export function isValidProjectCount(count: number): boolean {
	return count >= 0 && count <= MAX_PREVIOUS_PROJECTS;
}

// ============================================================================
// TYPES
// ============================================================================

/**
 * Previous project data structure
 * Used in both frontend and backend
 */
export interface PreviousProject {
	id: string;
	profileType: "designer" | "contractor";
	profileId: string;
	title: string;
	description: string | null;
	year: number | null;
	location: string | null;
	budgetMin: number | null;
	budgetMax: number | null;
	durationMonths: number | null;
	projectType: PreviousProjectType | null;
	status: PreviousProjectStatus | null;
	imageUrls: string[];
	sortOrder: number;
	createdAt: string;
	updatedAt: string;
}

/**
 * Input type for creating/updating previous projects
 * All fields optional except title
 */
export type PreviousProjectInput = Omit<
	Partial<PreviousProject>,
	| "id"
	| "profileType"
	| "profileId"
	| "createdAt"
	| "updatedAt"
	| "imageUrls"
	| "sortOrder"
> & {
	title: string;
	imageUrls?: string[];
	sortOrder?: number;
};

/**
 * Validation error type
 */
export interface ValidationError {
	field: string;
	message: string;
}

/**
 * Validates a previous project input against the validation rules
 * Returns array of validation errors (empty if valid)
 */
export function validatePreviousProject(
	input: PreviousProjectInput,
): ValidationError[] {
	const errors: ValidationError[] = [];

	// Title validation
	if (!input.title || input.title.trim().length === 0) {
		errors.push({ field: "title", message: "Title is required" });
	} else if (input.title.length > PREVIOUS_PROJECT_VALIDATION.title.maxLength) {
		errors.push({
			field: "title",
			message: `Title must be less than ${PREVIOUS_PROJECT_VALIDATION.title.maxLength} characters`,
		});
	}

	// Description validation
	if (
		input.description &&
		input.description.length > PREVIOUS_PROJECT_VALIDATION.description.maxLength
	) {
		errors.push({
			field: "description",
			message: `Description must be less than ${PREVIOUS_PROJECT_VALIDATION.description.maxLength} characters`,
		});
	}

	// Year validation
	if (input.year !== undefined && input.year !== null) {
		if (!isValidYear(input.year)) {
			errors.push({
				field: "year",
				message: `Year must be between ${PREVIOUS_PROJECT_VALIDATION.year.min} and ${PREVIOUS_PROJECT_VALIDATION.year.max}`,
			});
		}
	}

	// Location validation
	if (
		input.location &&
		input.location.length > PREVIOUS_PROJECT_VALIDATION.location.maxLength
	) {
		errors.push({
			field: "location",
			message: `Location must be less than ${PREVIOUS_PROJECT_VALIDATION.location.maxLength} characters`,
		});
	}

	// Budget validation
	if (input.budgetMin !== undefined && input.budgetMin !== null) {
		if (input.budgetMin < PREVIOUS_PROJECT_VALIDATION.budgetMin.min) {
			errors.push({
				field: "budgetMin",
				message: "Minimum budget cannot be negative",
			});
		}
	}

	if (input.budgetMax !== undefined && input.budgetMax !== null) {
		if (input.budgetMax < PREVIOUS_PROJECT_VALIDATION.budgetMax.min) {
			errors.push({
				field: "budgetMax",
				message: "Maximum budget cannot be negative",
			});
		}
	}

	if (!isValidBudgetRange(input.budgetMin, input.budgetMax)) {
		errors.push({
			field: "budgetMax",
			message: "Maximum budget must be greater than or equal to minimum budget",
		});
	}

	// Duration validation
	if (input.durationMonths !== undefined && input.durationMonths !== null) {
		const { min, max } = PREVIOUS_PROJECT_VALIDATION.durationMonths;
		if (input.durationMonths < min || input.durationMonths > max) {
			errors.push({
				field: "durationMonths",
				message: `Duration must be between ${min} and ${max} months`,
			});
		}
	}

	// Project type validation
	if (input.projectType !== undefined && input.projectType !== null) {
		if (!isValidProjectType(input.projectType)) {
			errors.push({
				field: "projectType",
				message: `Project type must be one of: ${PREVIOUS_PROJECT_TYPES.join(", ")}`,
			});
		}
	}

	// Status validation
	if (input.status !== undefined && input.status !== null) {
		if (!isValidProjectStatus(input.status)) {
			errors.push({
				field: "status",
				message: `Status must be one of: ${PREVIOUS_PROJECT_STATUSES.join(", ")}`,
			});
		}
	}

	// Image URLs validation
	if (
		input.imageUrls &&
		input.imageUrls.length > PREVIOUS_PROJECT_VALIDATION.imageUrls.maxItems
	) {
		errors.push({
			field: "imageUrls",
			message: `Maximum ${PREVIOUS_PROJECT_VALIDATION.imageUrls.maxItems} images allowed`,
		});
	}

	return errors;
}
